import React from "react";
import AdminLayout from "../../../components/AdminLayout";
import { Button, Table, Tabs, Tag, Input, Modal, type TableProps, type TabsProps, Dropdown, Popover, Alert } from "antd";
import { DeleteOutlined, MoreOutlined, PlusOutlined, SearchOutlined, ExclamationCircleOutlined } from "@ant-design/icons";
import "./index.css";
import { useNavigate } from "react-router-dom";

import { useContentManagement } from "../ContentManagement/hooks/useContentManagement";
import { formatThaiDate } from "../../../utils/dateFormatter";
import { useUserInfo } from "../../../hooks/useUserInfo";

export interface ContentDataType {
  key: string;
  sk: string;
  dxNumber: string;
  title: string;
  creator: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  version: string;
  status: string;
  edit_status: string;
  content_owner: string;
  content_members: string[];
}

const DxManagement: React.FC = () => {
  // API integration
  const {
    tableData: allTableData,
    isLoading,
    isDeleting,
    isUpdating,
    error,
    deleteContent,
    clearError
  } = useContentManagement();

  // User info hook
  const { userInfo, isLoading: isLoadingUserInfo } = useUserInfo();

  // Local state
  const [searchText, setSearchText] = React.useState("");
  const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
  const [itemToDelete, setItemToDelete] = React.useState<ContentDataType | null>(null);
  const [currentFilter, setCurrentFilter] = React.useState<string>('');
  const [isCreating] = React.useState(false);
  const navigate = useNavigate();

  // Determine user role and permissions
  const userRole = React.useMemo(() => {
    if (!userInfo?.roles || userInfo.roles.length === 0) return null;
    
    if (userInfo.roles.includes('super-admin')) return 'super-admin';
    if (userInfo.roles.includes('admin')) return 'admin';
    if (userInfo.roles.includes('content-owner')) return 'content-owner';
    if (userInfo.roles.includes('content-member')) return 'content-member';
    
    return null;
  }, [userInfo]);

  const userEmail = userInfo?.email;

  // Check if user can create content (only super-admin)
  const canCreateContent = userRole === 'super-admin';

  // Check if user can delete content (only super-admin)
  const canDeleteContent = userRole === 'super-admin';

  // Client-side filtered data
  const tableData = React.useMemo(() => {
    let filtered = allTableData;

    // Apply role-based filtering
    // Note: A user can be both content-owner for some content AND content-member for others
    if ((userRole === 'content-owner' || userRole === 'content-member') && userEmail) {
      filtered = filtered.filter(item => {
        // Show if user is the owner
        const isOwner = item.content_owner === userEmail;
        // Show if user is a member
        const isMember = item.content_members && item.content_members.includes(userEmail);
        
        return isOwner || isMember;
      });
    }
    // super-admin and admin can see all content, so no filtering needed

    if (currentFilter === 'progress') {
      filtered = filtered.filter(item => item.status === 'draft' || item.status === 'progress');
    } else if (currentFilter === 'published') {
      filtered = filtered.filter(item => item.status === 'published');
    }

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item => 
        item.key.toLowerCase().includes(searchLower) ||
        item.title.toLowerCase().includes(searchLower) ||
        item.creator.toLowerCase().includes(searchLower) ||
        item.updatedBy.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [allTableData, currentFilter, searchText, userRole, userEmail]);

  // Calculate filtered counts based on user role
  const filteredCounts = React.useMemo(() => {
    let roleFilteredData = allTableData;

    // Apply role-based filtering
    // Note: A user can be both content-owner for some content AND content-member for others
    if ((userRole === 'content-owner' || userRole === 'content-member') && userEmail) {
      roleFilteredData = roleFilteredData.filter(item => {
        // Show if user is the owner
        const isOwner = item.content_owner === userEmail;
        // Show if user is a member
        const isMember = item.content_members && item.content_members.includes(userEmail);
        
        return isOwner || isMember;
      });
    }

    const total = roleFilteredData.length;
    const progress = roleFilteredData.filter(item => item.status === 'draft' || item.status === 'progress').length;
    const published = roleFilteredData.filter(item => item.status === 'published').length;

    return { total, progress, published };
  }, [allTableData, userRole, userEmail]);

  const handleDeleteClick = (item: ContentDataType) => {
    setItemToDelete(item);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      const success = await deleteContent(itemToDelete);
      if (success) {
        setDeleteModalVisible(false);
        setItemToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setItemToDelete(null);
  };

  const handleTabChange = (activeKey: string) => {
    setCurrentFilter(activeKey === 'overview' ? '' : activeKey);
  };

  const handleCreateNewContent = async () => {
    navigate(`/admin/dx-list/new`);
  };

  const contentColumns: TableProps<ContentDataType>['columns'] = [
    {
      title: 'รหัส',
      dataIndex: 'key',
      key: 'key',
      render: (text: string) => <p>{text}</p>,
      fixed: 'left',
      width: 50,
    },
    {
      title: 'หัวข้อ',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: ContentDataType) => (
        <Popover content={
          <div style={{ maxWidth: 300 }}>
            <p style={{ color: 'white' }}>{text}</p>
          </div>
        } color={"rgba(250, 140, 22, 1)"}>
          <a
            style={{ color: "rgba(250, 140, 22, 1)" }}
            onClick={() => window.location.href = `/admin/dx-list/${record.sk}${record.status === 'published' ? `?version=v${record.version}` : ''}`}
            href={`/admin/dx-list/${record.sk}${record.status === 'published' ? `?version=v${record.version}` : ''}`}
          >
            {text}
          </a>
        </Popover>
      ),
      fixed: 'left',
      width: 400,
    },
    {
      title: 'ผู้รับผิดชอบหลัก',
      dataIndex: 'creator',
      key: 'creator',
      render: (text) => <p>{text}</p>,
    },
    {
      title: 'วันที่สร้าง',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => <p>{formatThaiDate(text)}</p>,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      render: (_, { status }) => {
        let color = '';
        let text = '';

        if (status === 'published') {
          color = 'green';
          text = 'เผยแพร่แล้ว';
        } else if (status === 'draft' || status === 'progress') {
          color = 'orange';
          text = 'แบบร่าง';
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'ผู้มีสิทธิ์แก้ไข',
      dataIndex: 'editorCount',
      key: 'editorCount',
      render: (_, record) => {
        // Count content_owner (1) + content_members (excluding empty strings)
        const ownerCount = record.content_owner ? 1 : 0;
        const membersCount = record.content_members 
          ? record.content_members.filter(email => email && email.trim() !== '').length 
          : 0;
        const totalEditors = ownerCount + membersCount;
        
        return <p>{totalEditors}</p>;
      },
    },
    {
      title: 'จัดการ',
      key: 'edit',
      render: (_, record) => {
        // Only show delete option for super-admin users
        if (!canDeleteContent) {
          return (
            <Button 
              icon={<MoreOutlined />} 
              type="text" 
              disabled 
              title="ไม่มีสิทธิ์ในการจัดการ"
            />
          );
        }

        return (
          <div>
            <Dropdown
              menu={{
                items: [
                  {
                    key: 'delete',
                    label: 'ลบรายการ',
                    icon: <DeleteOutlined />,
                    onClick: () => handleDeleteClick(record),
                    disabled: isDeleting,
                  },
                ],
              }}
              trigger={['click']}
            >
              <Button icon={<MoreOutlined />} type="text" disabled={isDeleting || isUpdating} />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const contentTable = () => {
    // Show loading state if either user info or content is loading
    const isTableLoading = isLoading || isLoadingUserInfo;
    
    // Only show data when both user info and content are loaded
    const safeTableData = isLoadingUserInfo ? [] : tableData;

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {error && (
              <Alert
                message="Error loading data"
                description={error}
                type="error"
                closable
                onClose={clearError}
                style={{ flex: 1, maxWidth: 400 }}
              />
            )}
          </div>
          <Input
            placeholder="ค้นหารายการ"
            prefix={<SearchOutlined style={{ color: 'gray' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
        </div>
        <Table 
          columns={contentColumns} 
          dataSource={safeTableData} 
          loading={isTableLoading}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            defaultPageSize: 20,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
        />
      </div>
    );
  }

  const tabs: TabsProps['items'] = [
    {
      key: 'overview',
      label: 'รายการทั้งหมด' + (filteredCounts.total > 0 ? ` (${filteredCounts.total})` : ''),
      children: contentTable(),
    },
    {
      key: 'progress',
      label: 'แบบร่าง' + (filteredCounts.progress > 0 ? ` (${filteredCounts.progress})` : ''),
      children: contentTable(),
    },
    {
      key: 'published',
      label: 'เผยแพร่แล้ว' + (filteredCounts.published > 0 ? ` (${filteredCounts.published})` : ''),
      children: contentTable(),
    },
  ];

  return (
    <AdminLayout children={
      <div>
        <div className="header-container">
          <h3>จัดการ DX</h3>
          {canCreateContent && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={handleCreateNewContent}
              loading={isCreating}
            >
              {"เพิ่มหัวข้อ (DX)"}
            </Button>
          )}
        </div>
        
        <Tabs defaultActiveKey="overview" items={tabs} onChange={handleTabChange} />

        <Modal
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 16 }}>
              <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
              <span>ยืนยันการลบรายการ</span>
            </div>
          }
          open={deleteModalVisible}
          onOk={handleDeleteConfirm}
          onCancel={handleDeleteCancel}
          okText="ยืนยัน"
          cancelText="ยกเลิก"
          centered
        >
          <p>คุณต้องการลบรายการ {itemToDelete?.key}: {itemToDelete?.title} หรือไม่?</p>
        </Modal>
      </div>
    } breadcrumb={{
      items: [{ title: "ตั้งค่า" }, { title: "จัดการ DX" }],
    }}>
    </AdminLayout>
  );
};

export default DxManagement;
